/**
 * Global Error Boundary
 *
 * Catches errors that escape the app-level error.tsx (e.g., root layout errors).
 * This is the last line of defense for React errors.
 */

'use client';

import { useEffect } from 'react';
import posthog from 'posthog-js';
import { attemptChunkReload, isChunkLoadError } from '@/lib/chunk-reload';

export default function GlobalError({
  error,
  reset,
}: {
  error: Error & { digest?: string };
  reset: () => void;
}) {
  const chunkError = isChunkLoadError(error);

  useEffect(() => {
    console.error('Global application error:', error);

    // Capture to PostHog error tracking
    if (typeof window !== 'undefined' && process.env.NEXT_PUBLIC_POSTHOG_KEY) {
      posthog.captureException(error, {
        $exception_source: 'global_error_boundary',
        $exception_type: error.name,
        digest: error.digest,
      });
    }
  }, [error]);

  // gh #825: same stale-build recovery as app/error.tsx — reset() cannot fix a
  // chunk 404. Shares the loop guard, so whichever boundary catches it first
  // consumes the single reload attempt.
  useEffect(() => {
    attemptChunkReload(error);
  }, [error]);

  return (
    <html>
      <body>
        <div className="min-h-screen flex items-center justify-center bg-gray-50">
          <div className="max-w-md w-full bg-white rounded-lg shadow-lg p-8 text-center">
            <div className="text-6xl mb-4">⚠️</div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Something went wrong!</h2>
            <p className="text-gray-600 mb-6">
              {chunkError
                ? 'The app was updated while this page was open. Reloading picks up the new version.'
                : 'A critical error occurred. Please try refreshing the page.'}
            </p>
            <button
              onClick={chunkError ? () => window.location.reload() : reset}
              className="bg-blue-600 text-white px-6 py-2 rounded hover:bg-blue-700 transition-colors"
            >
              {chunkError ? 'Reload page' : 'Try again'}
            </button>
          </div>
        </div>
      </body>
    </html>
  );
}
